import { useCallback, useEffect, useRef, useState } from "react";
import { throttle } from "lodash";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import COMMON from "common";
import pathnames from "routes/pathnames";

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const THROTTLE_WAIT = 1000;

const useInactivityLogout = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const timeoutRef = useRef(null);
	const [isInactive, setIsInactive] = useState(false);

	const onHandleInvalidateSession = useCallback(() => {
		localStorage.clear();
		sessionStorage.clear();
		dispatch({ type: COMMON.REDUX_ACTION.LOGOUT });
	}, [dispatch]);

	const onHandleSessionExpire = useCallback(() => {
		onHandleInvalidateSession();
		setIsInactive(true);
	}, [onHandleInvalidateSession]);

	const onHandleConfirmLogout = useCallback(() => {
		onHandleInvalidateSession();
		navigate(pathnames.login);
		setIsInactive(false);
	}, [onHandleInvalidateSession, navigate]);

	useEffect(() => {
		if (isInactive) return undefined;

		const onHandleResetTimer = () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(onHandleSessionExpire, INACTIVITY_TIMEOUT);
		};

		const onHandleThrottledReset = throttle(onHandleResetTimer, THROTTLE_WAIT);

		onHandleResetTimer();

		window.addEventListener("mousemove", onHandleThrottledReset);
		window.addEventListener("mousedown", onHandleResetTimer);
		window.addEventListener("keydown", onHandleResetTimer);
		window.addEventListener("touchstart", onHandleResetTimer);
		window.addEventListener("scroll", onHandleThrottledReset, { capture: true });

		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			onHandleThrottledReset.cancel();

			window.removeEventListener("mousemove", onHandleThrottledReset);
			window.removeEventListener("mousedown", onHandleResetTimer);
			window.removeEventListener("keydown", onHandleResetTimer);
			window.removeEventListener("touchstart", onHandleResetTimer);
			window.removeEventListener("scroll", onHandleThrottledReset, { capture: true });
		};
	}, [location.pathname, isInactive, onHandleSessionExpire]);

	return { isInactive, onHandleConfirmLogout };
};

export default useInactivityLogout;
